import { useCallback, useEffect, useRef, useState } from 'react';
import { animate } from 'framer-motion';

const DELIMITER = ' ';
const DURATION_PER_CHAR = 0.03;

export type UseStreamBufferOptions = {
  onUpdate: (slice: string) => void;
  onComplete: () => void;
};

export type StreamBuffer = {
  feed: (content: string) => void;
  stop: () => void;
  flushComplete: () => void;
};

export const useStreamBuffer = ({
  onUpdate,
  onComplete,
}: UseStreamBufferOptions): StreamBuffer => {
  const [targetContent, setTargetContent] = useState('');
  const targetContentRef = useRef('');
  const cursorRef = useRef(0);
  const startingCursorRef = useRef(0);
  const prevTargetRef = useRef('');
  const controlsRef = useRef<ReturnType<typeof animate> | null>(null);
  const abortedRef = useRef(false);

  const stop = useCallback(() => {
    abortedRef.current = true;
    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }
    cursorRef.current = 0;
    startingCursorRef.current = 0;
    prevTargetRef.current = '';
    targetContentRef.current = '';
    setTargetContent('');
    onUpdate('');
  }, [onUpdate]);

  const feed = useCallback((content: string) => {
    abortedRef.current = false;
    targetContentRef.current = content;
    setTargetContent(content);
  }, []);

  const flushComplete = useCallback(() => {
    if (abortedRef.current) {
      return;
    }
    const content = targetContentRef.current;
    if (content === '') {
      onComplete();
      return;
    }
    const parts = content.split(DELIMITER);
    const targetLength = parts.length;
    if (targetLength === 0) {
      onComplete();
      return;
    }
    if (cursorRef.current >= targetLength) {
      onUpdate(content);
      onComplete();
    }
  }, [onUpdate, onComplete]);

  useEffect(() => {
    if (!targetContent) {
      onUpdate('');
      return;
    }

    const parts = targetContent.split(DELIMITER);
    const targetLength = parts.length;

    if (targetLength === 0) {
      onUpdate('');
      return;
    }

    const prevTarget = prevTargetRef.current;
    const isPrefix = targetContent.startsWith(prevTarget);
    const startingCursor = isPrefix ? cursorRef.current : 0;

    startingCursorRef.current = startingCursor;
    prevTargetRef.current = targetContent;
    targetContentRef.current = targetContent;

    if (startingCursor >= targetLength) {
      onUpdate(targetContent);
      if (!abortedRef.current) {
        onComplete();
      }
      cursorRef.current = targetLength;
      return;
    }

    const deltaLength = targetLength - startingCursor;
    const duration = Math.min(Math.max(deltaLength * DURATION_PER_CHAR, 0.1), 4);

    if (controlsRef.current) {
      controlsRef.current.stop();
    }

    const animationId = Symbol('animation');
    let currentAnimationId = animationId;

    controlsRef.current = animate(startingCursor, targetLength, {
      duration,
      ease: 'easeOut',
      onUpdate: (latest) => {
        const cursor = Math.floor(latest);
        cursorRef.current = cursor;
        const slice = parts.slice(0, cursor).join(DELIMITER);
        onUpdate(slice);
      },
    });

    controlsRef.current.then(() => {
      if (currentAnimationId !== animationId || abortedRef.current) {
        return;
      }
      controlsRef.current = null;
      cursorRef.current = targetLength;
      onUpdate(targetContent);
      onComplete();
    });

    return () => {
      currentAnimationId = Symbol('cancelled');
      if (controlsRef.current) {
        controlsRef.current.stop();
      }
    };
  }, [targetContent, onUpdate, onComplete]);

  return { feed, stop, flushComplete };
};
